/**
 * Event Columns for DataTable
 * 
 * Column definitions for event management tables
 */

import { type FC } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { MdOutlineVisibility, FiFileText } from '@/config/icons';
import { StatusBadge } from '@/components/Common/StatusBadge';
import Button from '@/components/Common/Button';
import type { EventDetailsResponse } from '@/types';
import styles from './eventColumns.module.css';

// Format date helper
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Format seconds to readable time
const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '-';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Event Actions Props
 */
export interface EventActionsProps {
  onViewLogs?: (event: EventDetailsResponse) => void;
}

/**
 * Create event columns for TanStack Table
 */
export const createEventColumns = (actions: EventActionsProps): ColumnDef<EventDetailsResponse>[] => [
  {
    id: 'id',
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <span className={styles.id}>#{row.original.id}</span>
    ),
  },
  {
    id: 'agentName',
    accessorKey: 'agentName',
    header: 'Agente',
    cell: ({ row }) => (
      <div className={styles.agentCell}>
        <span className={styles.agentName}>
          {row.original.agentName || 'Sem nome'}
        </span>
        <span className={styles.agentRef} title={row.original.agentRef}>
          {row.original.agentRef.substring(0, 8)}...
        </span>
      </div>
    ),
  },
  {
    id: 'eventType',
    accessorKey: 'eventType',
    header: 'Tipo',
    cell: ({ row }) => (
      <StatusBadge status={row.original.eventType} type="eventType" />
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusBadge status={row.original.status} type="event" />
    ),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Criado em',
    cell: ({ row }) => (
      <span className={styles.date}>{formatDate(row.original.createdAt)}</span>
    ),
  },
  {
    id: 'processedAt',
    accessorKey: 'processedAt',
    header: 'Processado em',
    cell: ({ row }) => (
      <span className={styles.date}>{formatDate(row.original.processedAt)}</span>
    ),
  },
  {
    id: 'pendingForSeconds',
    accessorKey: 'pendingForSeconds',
    header: 'Tempo Pendente',
    cell: ({ row }) => (
      <span
        className={styles.duration}
        data-stuck={row.original.status === 'STUCK'}
      >
        {formatDuration(row.original.pendingForSeconds)}
      </span>
    ),
  },
  {
    id: 'actions',
    header: 'Ações',
    cell: ({ row }) => {
      const event = row.original;
      
      return (
        <div className={styles.actionsWrapper}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onViewLogs?.(event)}
            title="Ver logs do evento"
            iconLeft={<FiFileText size={16} />}
          />
        </div>
      );
    },
    enableSorting: false,
    size: 80,
  },
];

/**
 * Default event columns (for backward compatibility)
 */
export const eventColumns: ColumnDef<EventDetailsResponse>[] = createEventColumns({});

/**
 * Expandable row content for event payload
 */
export const EventPayloadDetails: FC<{ event: EventDetailsResponse }> = ({ event }) => {
  return (
    <div className={styles.payloadContainer}>
      <h4 className={styles.payloadTitle}>
        <MdOutlineVisibility />
        Detalhes do Payload
      </h4>
      <pre className={styles.payloadContent}>
        {JSON.stringify(event.payload, null, 2)}
      </pre>
    </div>
  );
};
